import starfieldV3Url from '../../assets/source/starfield/starfield-v3.webp';
import { Atlas, type Sprite, type SpriteCss } from './atlas';
import { backgroundStore, type BackgroundStyle } from './background-store';
import { BASE_SPRITE_DARKEN, TEAM_SPRITE_COLORS } from '../game/constants/teams';
import { limeHuedSprite, tintSprite } from './tint';

declare const __LEGACY_ASSET_PREFIX__: string;

export const ASSET_PREFIX: string =
  typeof __LEGACY_ASSET_PREFIX__ === 'string' ? __LEGACY_ASSET_PREFIX__ : '/legacy-assets';

export type TeamArtKind = 'ship' | 'drone' | 'turret' | 'base' | 'baseIcon';

const SCRAP_VARIANTS = 4;

export interface HueShift {
  readonly hue: number;
  readonly saturation: number;
  readonly luminance: number;
}

export const COSMETIC_HUES: Readonly<Record<'decal' | 'skin', readonly HueShift[]>> = {
  decal: [
    { hue: 120, saturation: -60, luminance: 0 },
    { hue: 240, saturation: -40, luminance: 2 },
  ],
  skin: [
    { hue: 65, saturation: -5, luminance: 0 },
    { hue: 235, saturation: -5, luminance: 0 },
  ],
};

export const sharedAtlas = new Atlas(ASSET_PREFIX);

const atlas = sharedAtlas;

export const AssetPaths = {
  ui: (name: string) => single(`UIAssets/${name}.png`),
  frame: (name: string, slice: string) => single(`frames/${name}/${slice}.png`),
  buttonSlice: (slice: 'left' | 'mid' | 'right') => single(`barHorizontal_blue_${slice}.png`),
  audioIcon: (on: boolean) => single(`audio${on ? 'On' : 'Off'}.png`),
  background: (style: BackgroundStyle) => {
    if (style === 'v3') return starfieldV3Url;
    return single(style === 'v1' ? 'bluebackgroundOld.png' : 'bluebackground.png');
  },
  font: () => `${ASSET_PREFIX}/xirod.ttf`,
  music: (file: string) => `${ASSET_PREFIX}/${file}`,
} as const;

function single(pngPath: string): string {
  return atlas.single(pngPath) ?? `${ASSET_PREFIX}/${pngPath.replace(/\.png$/, '')}.webp`;
}

export class AssetStore {
  private pattern: CanvasPattern | null = null;
  private patternImage: HTMLImageElement | null = null;
  private patternStyle: BackgroundStyle | null = null;
  private readonly tintCache = new Map<string, HTMLCanvasElement>();
  private readonly mipCache = new Map<string, HTMLCanvasElement>();

  readonly atlas = atlas;

  constructor() {
    void atlas.load().catch(() => undefined);
  }

  hullSprite(kind: TeamArtKind, id: number): Sprite | null {
    return atlas.sprite(`${kind}Hull/${String(id)}`);
  }

  detailSprite(kind: TeamArtKind, id: number): Sprite | null {
    return atlas.sprite(`${kind}Detail/${String(id)}`);
  }

  tintedSprite(kind: TeamArtKind, team: number, id: number): Sprite | null {
    const color = TEAM_SPRITE_COLORS[team] ?? TEAM_SPRITE_COLORS[0];
    const key = `${kind}:${color}:${String(id)}`;
    const cached = this.tintCache.get(key);
    if (cached !== undefined) {
      return { image: cached, sx: 0, sy: 0, sw: cached.width, sh: cached.height };
    }
    const hull = this.hullSprite(kind, id);
    const detail = this.detailSprite(kind, id);
    if (hull === null || detail === null) return null;
    const canvas = tintSprite(hull, detail, color, kind === 'base' ? BASE_SPRITE_DARKEN : 0);
    if (canvas === null) return null;
    this.tintCache.set(key, canvas);
    return { image: canvas, sx: 0, sy: 0, sw: canvas.width, sh: canvas.height };
  }

  /** Never returns a level below `devicePx`: callers draw at their own size and would upscale. */
  tintedSpriteFor(kind: TeamArtKind, team: number, id: number, devicePx: number): Sprite | null {
    const full = this.tintedSprite(kind, team, id);
    if (full === null) return null;
    const source = full.image as HTMLCanvasElement;
    const level = mipWidth(source.width, devicePx);
    if (level >= source.width) return full;

    const color = TEAM_SPRITE_COLORS[team] ?? TEAM_SPRITE_COLORS[0];
    const key = `${kind}:${color}:${String(id)}:${String(level)}`;
    const cached = this.mipCache.get(key);
    if (cached !== undefined) {
      return { image: cached, sx: 0, sy: 0, sw: cached.width, sh: cached.height };
    }
    const scaled = downscale(source, level);
    if (scaled === null) return full;
    this.mipCache.set(key, scaled);
    return { image: scaled, sx: 0, sy: 0, sw: scaled.width, sh: scaled.height };
  }

  /** Never returns a level below `devicePx`. Skins are 512x512 and decals 256x256 over a ~130 px ship, so both minified the full source every frame. */
  cosmeticSpriteFor(imagePath: string, shift: HueShift | null, devicePx: number): Sprite | null {
    const full = this.cosmeticSprite(imagePath, shift);
    if (full === null) return null;
    const level = mipWidth(full.sw, devicePx);
    if (level >= full.sw) return full;

    const key = `cosmetic:${imagePath}:${String(shift?.hue ?? 'raw')}:${String(level)}`;
    const cached = this.mipCache.get(key);
    if (cached !== undefined) {
      return { image: cached, sx: 0, sy: 0, sw: cached.width, sh: cached.height };
    }
    const scaled = downscaleSprite(full, level);
    if (scaled === null) return full;
    this.mipCache.set(key, scaled);
    return { image: scaled, sx: 0, sy: 0, sw: scaled.width, sh: scaled.height };
  }

  tintedSpriteCss(kind: TeamArtKind, team: number, id: number, size: number): SpriteCss | null {
    const sprite = this.tintedSprite(kind, team, id);
    if (sprite === null) return null;
    const canvas = sprite.image as HTMLCanvasElement;
    const scale = size / Math.max(canvas.width, canvas.height);
    return {
      url: canvas.toDataURL(),
      backgroundSize: `${String(canvas.width * scale)}px ${String(canvas.height * scale)}px`,
      backgroundPosition: '0 0',
      width: canvas.width * scale,
      height: canvas.height * scale,
    };
  }

  cosmeticCss(imagePath: string, shift: HueShift | null, size: number): SpriteCss | null {
    if (shift === null) return atlas.spriteCssStretched(imagePath, size);
    const sprite = this.cosmeticSprite(imagePath, shift);
    if (sprite === null) return null;
    const canvas = sprite.image as HTMLCanvasElement;
    return {
      url: canvas.toDataURL(),
      backgroundSize: `${String(size)}px ${String(size)}px`,
      backgroundPosition: '0 0',
      width: size,
      height: size,
    };
  }

  controlPointSprite(pointType: number): Sprite | null {
    return atlas.sprite(`controlPoint/${String(pointType)}`);
  }

  soccerBallSprite(): Sprite | null {
    return atlas.sprite('soccerBall');
  }

  coinSprite(): Sprite | null {
    return atlas.sprite('coin');
  }

  damageBuffSprite(): Sprite | null {
    return atlas.sprite('damageBuff');
  }

  emojiSprite(index: number): Sprite | null {
    return atlas.sprite(`emoji/${String(index)}`);
  }

  explosionSprite(variant: number): Sprite | null {
    return atlas.sprite(`explosion/${String(variant)}`);
  }

  trailSprite(name: string): Sprite | null {
    return atlas.sprite(`trail/${name}`);
  }

  cosmeticSprite(imagePath: string, shift: HueShift | null): Sprite | null {
    const sprite = atlas.sprite(imagePath);
    if (sprite === null || shift === null) return sprite;

    const key = `cosmetic:${imagePath}:${String(shift.hue)}`;
    const cached = this.tintCache.get(key);
    if (cached !== undefined) {
      return { image: cached, sx: 0, sy: 0, sw: cached.width, sh: cached.height };
    }
    const canvas = limeHuedSprite(sprite, shift.hue, shift.saturation, shift.luminance);
    if (canvas === null) return null;
    this.tintCache.set(key, canvas);
    return { image: canvas, sx: 0, sy: 0, sw: canvas.width, sh: canvas.height };
  }

  /** Keyed off the id, not random — a re-pick on every replication re-entry would visibly flicker. */
  scrapSprite(entityId: number): Sprite | null {
    const variant = ((entityId % SCRAP_VARIANTS) + SCRAP_VARIANTS) % SCRAP_VARIANTS;
    return atlas.sprite(`scrap/${String(variant)}`);
  }

  get backgroundImage(): HTMLImageElement | null {
    this.ensureBackgroundImage();
    return this.patternImage;
  }

  private ensureBackgroundImage(): void {
    const style = backgroundStore.style;
    if (style !== this.patternStyle) {
      this.patternStyle = style;
      this.pattern = null;
      this.patternImage = null;
      const image = new Image();
      image.onload = () => {
        if (this.patternStyle === style) this.patternImage = image;
      };
      image.src = AssetPaths.background(style);
    }
  }

  backgroundPattern(ctx: CanvasRenderingContext2D): CanvasPattern | null {
    this.ensureBackgroundImage();
    if (this.pattern !== null) return this.pattern;
    if (this.patternImage === null) return null;
    this.pattern = ctx.createPattern(this.patternImage, 'repeat');
    return this.pattern;
  }

  async loadFont(): Promise<void> {
    try {
      const face = new FontFace('xirod', `url(${AssetPaths.font()})`);
      await face.load();
      document.fonts.add(face);
    } catch {}
  }
}

export const sharedAssets = new AssetStore();

const MIN_MIP_PX = 32;

export function mipWidth(sourceWidth: number, devicePx: number): number {
  let width = sourceWidth;
  while (width > MIN_MIP_PX && width / 2 >= devicePx) width /= 2;
  return width;
}

function downscale(source: HTMLCanvasElement, width: number): HTMLCanvasElement | null {
  return downscaleSprite(
    { image: source, sx: 0, sy: 0, sw: source.width, sh: source.height },
    width,
  );
}

function downscaleSprite(sprite: Sprite, width: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const height = Math.max(1, Math.round((sprite.sh * width) / sprite.sw));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(sprite.image, sprite.sx, sprite.sy, sprite.sw, sprite.sh, 0, 0, width, height);
  return canvas;
}
